<template>
  <!-- 动态地铁网络背景 -->
  <MetroNetworkBackground 
    ref="metroNetworkRef" 
    :visible="networkVisible" 
    :showUnopenedSegments="showUnopenedSegments" 
  />
  
  <!-- 独立的线路图控制按钮 - 只在主内容显示后出现，且不受线路图状态影响 -->
  <transition name="control-fade">
    <div v-if="showMainContent || networkVisible" class="metro-control-wrapper">
      <div class="toggle-button-container" 
           @mouseenter="showTooltip = true" 
           @mouseleave="showTooltip = false">
        <img src="/map-switch.svg" 
             alt="线路图切换" 
             class="metro-network-toggle" 
             @click="toggleNetworkVisibility" />
        
        <!-- 即时提示 -->
        <transition name="tooltip-fade">
          <div v-if="showTooltip" class="instant-tooltip">
            {{ networkVisible ? '隐藏线路图' : '显示线路图' }}
          </div>
        </transition>
      </div>
    </div>
  </transition>
  
  <!-- 主要内容区域 - 当显示线路图时隐藏 -->
  <div class="main-content" :class="{ hidden: networkVisible }">
    <!-- 动态地铁标志背景 - 铺满整个页面 -->
    <MetroSignGenerator ref="metroSignGenerator" @transition-complete="handleTransitionComplete" />
    
    <!-- 章节指示器 - 左侧蓝色色块 -->
    <transition name="section-indicator-fade">
      <div v-if="showMainContent && currentSectionIndex >= 0" class="section-indicator">
        <div class="section-indicator-content">
          <div class="section-chinese">{{ sectionData[currentSectionIndex]?.chinese }}</div>
          <div class="section-english">{{ sectionData[currentSectionIndex]?.english }}</div>
        </div>
      </div>
    </transition>
    
    <!-- 地铁风格进度条 - 只在开始浏览正式章节时显示 -->
    <transition name="progress-fade">
      <div v-if="showProgressBar" class="metro-progress">
        <div class="metro-progress-fill" 
             :style="{ 
               width: scrollProgress + '%',
               background: getCurrentMetroColor
             }">
          <!-- 线路编号指示器 - 位于进度条内部左侧 -->
          <transition name="line-indicator-fade" mode="out-in">
            <div v-if="scrollProgress > 5" class="metro-line-indicator" :key="getCurrentLineNumber">
              LINE {{ getCurrentLineNumber }}
            </div>
          </transition>
        </div>
        <!-- 地铁站点圆圈标识 - 位于进度条前端 -->
        <div class="metro-station-circle" 
             :style="{ 
               left: scrollProgress + '%',
               transform: 'translateX(-50%)'
             }"></div>
        <!-- 站点信息指示器 - 跟随进度移动 -->
        <!-- <div class="metro-station-indicator" 
             :style="{ 
               left: scrollProgress + '%',
               transform: 'translateX(-50%)'
             }">
          <div class="station-info">
            <div class="station-header">
              <img src="/shanghai-metro-logo.svg" alt="上海地铁" class="station-logo" />
              <span class="station-name">{{ currentStation }}</span>
            </div>
          </div>
        </div> -->
      </div>
    </transition>
  
  <div id="smooth-wrapper" :class="{ 'content-visible': showMainContent }">
    <div id="smooth-content">
      <!-- 主内容区域 - 长条形容器 -->
      <div class="page-background">
        <div class="content-container">
          <div class="app">
        <!-- 进站安检部分 -->
        <section class="data-section security-section">
          <div class="section-header">
            <h2 class="section-title">进站安检</h2>
            <p class="introduction">早高峰的上海街头，你匆匆走向地铁口，担心今天能否准时到公司、学校。熙熙攘攘的人群中，地铁入口仿佛都市脉搏的起点。但从1985年上海地铁公司成立，到如今40周年，进站引导真的清晰到让出行更便捷了吗？</p>
            <p class="section-subtitle">指引标志形式</p>
          </div>
          <div class="section-description">
            <p>上海地铁的指引标志系统经过多年发展，形成了多样化的标识形式。从传统的上墙式、立牌式到现代化的包柱式和敞开式出入口导向，每种形式都在特定场景下发挥着重要作用。合理的标志布局不仅能够提升乘客出行效率，更是城市公共交通人性化服务的重要体现。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <MetroSignageSystem />
            </div>
          </div>

          <div class="section-header">
            <p class="section-subtitle">地铁口数量分布</p>
          </div>
          <div class="section-description">
            <p>上海地铁各线路出入口数量差异显著，这与线路建设年代、沿线地理环境以及客流需求密切相关。早期建设的线路如1、2号线，由于当时技术限制和规划考虑，出入口相对较少；而近年来新建线路更注重便民性，出入口设置更为合理。通过数据分析，我们能够清楚地看到各线路在出入口配置上的差异。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <MetroGateChart />
            </div>
          </div>

          <div class="section-header">
            <p class="section-subtitle">无障碍设施配备</p>
          </div>
          <div class="section-description">
            <p>作为现代化都市轨道交通系统，上海地铁高度重视无障碍设施建设。电梯、扶梯等上下楼设备的配备不仅关乎残障人士的出行便利，更是老年人、孕妇、携带大件行李乘客的重要保障。近年来，随着城市包容性发展理念的推进，地铁站点无障碍设施覆盖率持续提升，体现了公共交通的人文关怀。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <h3>上海地铁各线路无障碍设施配备情况</h3>
              <MetroAccessibilityChart />
            </div>
          </div>
          <div class="section-header">
            <p class="introduction">早高峰的上海街头，你匆匆走向地铁口，担心今天能否准时到公司、学校。熙熙攘攘的人群中，地铁入口仿佛都市脉搏的起点。但从1985年上海地铁公司成立，到如今40周年，进站引导真的清晰到让出行更便捷了吗？</p>
            <p class="section-subtitle">安检政策变化</p>
          </div>
          <div class="section-description">
            <p>上海地铁安检从2008年局部试点起步，至2010年3月实现全网覆盖，后续结合反恐要求与便民需求持续优化流程，近年重点推进枢纽站点“免重复安检”服务，安检要求逐渐规范化。</p>
          </div>
            <div class="charts-grid">
            <div class="chart-container full-width">
              <h3>上海地铁安检政策发展时间轴</h3>
              <MetroSecurityTimeline />
            </div>
          </div>
        </section>

        <!-- 过闸机部分 -->
        <section class="data-section gate-section">
          <div class="section-header">
            <h2 class="section-title">过闸机</h2>
            <p class="introduction">拿着手机，揣着新买的票，你期待一刷即过，却也偶尔卡壳。2025年刷卡革命来袭，在支持数字人民币和国际卡的“无感”时代，过闸真的会更丝滑吗？</p>
            <p class="section-subtitle">票种类型与支付方式</p>
          </div>
          <div class="section-description">
            <p>上海地铁票务系统历经多次升级换代，从最初的纸质车票发展到如今的多元化支付生态。交通卡、手机NFC、二维码支付等多种方式并存，为不同年龄层和使用习惯的乘客提供了便利选择。这种包容性的票务设计，既保留了传统支付方式，又紧跟移动支付时代步伐。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <MetroTicketSystem />
            </div>
          </div>
          <div class="section-header">
            <p class="section-subtitle">闸机设备类型</p>
          </div>
          <div class="section-description">
            <p>上海地铁闸机系统采用多样化配置，包括标准闸机、宽通道闸机等不同类型。宽通道闸机专为轮椅使用者、携带大件行李或推婴儿车的乘客设计，体现了地铁系统的人性化考量。不同类型闸机的合理配比，确保了客流高峰期的通行效率，同时兼顾特殊需求乘客的便利性。</p>
          </div>
          
          <!-- 闸机设备类型图片展示 -->
          <div class="charts-grid">
            <div class="chart-container full-width">
              <h3>上海地铁闸机设备类型实拍</h3>
              <div class="gate-images-gallery">
                <div class="gate-image-item">
                  <img src="/三杆闸机.jpg" alt="标准三杆闸机" class="gate-image" />
                  <div class="gate-image-caption">
                    <h4>标准三杆闸机</h4>
                    <p>最常见的闸机类型，适用于大部分乘客日常通行，通过刷卡感应自动开闸</p>
                  </div>
                </div>
                
                <div class="gate-image-item">
                  <img src="/门式宽通道闸机.png" alt="门式宽通道闸机" class="gate-image" />
                  <div class="gate-image-caption">
                    <h4>门式宽通道闸机</h4>
                    <p>专为轮椅使用者、携带大件行李或推婴儿车的乘客设计，通道更宽敞</p>
                  </div>
                </div>
                
                <div class="gate-image-item">
                  <img src="/无障碍通道.jpg" alt="无障碍专用通道" class="gate-image" />
                  <div class="gate-image-caption">
                    <h4>无障碍专用通道</h4>
                    <p>配备残疾人标识的专用通道，确保行动不便的乘客能够顺利通行</p>
                  </div>
                </div>
              </div>
              <!-- 资料来源注释 -->
              <div class="data-source">
                资料来源：上海地铁官方网站
              </div>
            </div>
          </div>
        </section>

        <!-- 等候部分 -->
        <section class="data-section waiting-section">
          <div class="section-header">
            <h2 class="section-title">站台等候</h2>
            <p class="introduction">站台上，你盯着屏幕倒计时，空调凉风拂面，却担心高峰拥挤；有时身体不适，还需寻找可用的卫生间。这些影响候车体验的环节，上海地铁做得怎么样？</p>
            <p class="section-subtitle">站台服务设施</p>
          </div>
          <div class="section-description">
            <p>优质的站台服务设施是提升乘客候车体验的关键因素。上海地铁在站台设施配置方面不断完善，从基础的座椅、卫生间到便民服务设施，每一项配置都体现了对乘客需求的深度理解。卫生间作为重要的公共服务设施，其覆盖率和便民程度直接影响乘客的出行感受。</p>
          </div>
          <!-- 新增卫生间数据可视化 -->
          <div class="charts-grid">
            <div class="chart-container full-width">
              <h3>上海地铁各线路卫生间覆盖率分析</h3>
              <MetroToiletChart 
                :data="toiletData" 
                color="#06b6d4"
              />
            </div>
          </div>

          <div class="section-header">
            <p class="section-subtitle">列车运行间隔与等待时间</p>
          </div>
          <div class="section-description">
            <p>列车运行间隔直接影响乘客的等车体验和地铁系统的运营效率。上海地铁根据不同时段的客流特点，采用差异化的发车间隔策略。早晚高峰期间隔更短以应对大客流，非高峰时段适当延长间隔以优化运营成本。这种精细化的运营调度，平衡了服务质量与运营效率。</p>
          </div>
          <!-- 新增等车时长数据可视化 -->
          <div class="charts-grid">
              <MetroWaitingChart />
          </div>
        </section>

        <!-- 乘车部分 -->
        <section class="data-section riding-section">
          <div class="section-header">
            <h2 class="section-title">乘车</h2>
            <p class="introduction">车门开启，你跨越站台和车厢的缝隙进入列车，耳边是报站声和噪音混杂。声音和显示屏的结合让报站更清晰，但噪音和设备故障仍是痛点？</p>
            <p class="section-subtitle">常见乘车问题分析</p>
          </div>
          <div class="section-description">
            <p>通过对社交媒体平台乘客反馈的深度分析，我们识别出影响乘车体验的三大核心问题：车门与站台对齐精度、报站信息清晰度，以及车厢拥挤程度。这些问题不仅影响乘客的出行舒适度，也反映了轨道交通系统在技术升级和服务优化方面的发展空间。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <MetroRidingIssues />
            </div>
          </div>

          <div class="section-header">
            <p class="section-subtitle">站点间距离</p>
          </div>
          <div class="section-description">
            <p>通过分析上海地铁网络中所有相邻站点的地理坐标数据，我们计算出了各站点间的实际距离。数据显示，不同线路和区段的站点间距存在显著差异，这反映了上海地铁网络在不同区域的建设密度和地理条件的影响。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <h3>上海地铁相邻站点间直线距离 TOP10</h3>
              <MetroStationDistanceChart />
            </div>
          </div>
        </section>

        <!-- 换乘部分 -->
        <section class="data-section transfer-section">
          <div class="section-header">
            <h2 class="section-title">换乘</h2>
            <p class="introduction">走出车厢，面对长廊和指示牌，你心想：千万别走错！从虹桥2号航站楼的穿车厢换乘到出站换乘若干分钟的麻烦，指示足够清晰吗？数据将展现换乘距离、换乘时长和乘客疑惑，揭开换乘迷宫的秘密。</p>
            <p class="section-subtitle">换乘距离和时长</p>
          </div>
          <div class="section-description">
            <p>上海地铁网络中的换乘站点换乘时间差异较大，部分站点由于设计较早或地理限制，需要较长的换乘时间，甚至需要出站换乘。本图表展示了换乘时间前十长的站点，其中红色背景标识需要出站换乘的站点。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <h3>上海地铁换乘时长 TOP10</h3>
              <MetroTransferChart />
            </div>
          </div>

          <div class="section-header">
            <p class="section-subtitle">出站换乘站点数量</p>
          </div>
          <div class="section-description">
            <p>上海地铁网络中存在部分无法实现站内换乘的站点，乘客需要出站后重新进站完成线路换乘。这些出站换乘站点主要由于历史建设原因、地理条件限制或站点设计等因素造成，虽然增加了换乘时间和步行距离，但仍是地铁网络的重要组成部分。</p>
          </div>
          <div class="charts-grid">
              <MetroOutTransferChart />
          </div>

          <div class="section-header">
            <p class="section-subtitle">关于线路的疑惑</p>
          </div>
          <div class="section-description">
            <p>基于小红书等社交平台用户反馈，我们收集了大量关于上海地铁使用过程中的疑惑和困扰。通过对这些真实用户声音的分析，能够更好地了解乘客在实际使用地铁时遇到的问题，为地铁服务改进提供参考。</p>
          </div>
          <div class="charts-grid">
              <MetroConfusionWordCloud/>
          </div>
        </section>

        <!-- 线路网部分 -->
        <section class="data-section route-section">
          <div class="section-header">
            <h2 class="section-title">线路网</h2>
            <p class="introduction">终于出站，看着扣除的费用，你松了口气。这次旅程顺利吗？出口指示是否误导？回顾整趟旅程，从进站到出站，上海地铁的40年的发展凝结于此，体验真的升级了，还是仍有提升空间？</p>
            <p class="section-subtitle">城市覆盖度现状</p>
          </div>
          <div class="section-description">
            <p>《上海市轨道交通线网规划（2025—2035年）》提出，到2035年，上海将形成“总规模约2200公里”的轨道交通网络（含既有线、在建线及规划新增线路）。中心城轨道交通站点600米覆盖率达60%以上（现状约45%），新城轨道交通站点覆盖率不低于40%（现状部分新城不足20%）。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <h3>上海市轨道交通线网规划（2025—2035年）</h3>
              <MetroCoverageComparison />
            </div>
          </div>

          <div class="section-header">
            <p class="section-subtitle">未来发展规划</p>
          </div>
          <div class="section-description">
            <p>上海市政府常务会议原则批准《上海市轨道交通线网规划（2025—2035年）》，未来十年将大幅提升线网密度和站点覆盖率，强化与铁路的衔接，推动<span class='highlight'>站城融合</span>发展和优化换乘设施，旨在支撑城市新战略、新空间，并实现轨道交通与城市功能互促共振，让市民出行更便利、更高效。</p>
            <p>规划新增<span class="highlight">22条新线</span>，总里程冲刺<span class="highlight">1000公里</span>，覆盖嘉定、松江、青浦等郊区，形成<span class='highlight'>“组合环线”加强区域联动</span>。在建线路如火如荼：13号线西延伸、19号线等将于2025-2027年陆续开通，完善虹桥、宝山等区域；未来重点包括“史上最长大环线”26号线，串联11条线路缓解拥堵，以及市域铁路如机场联络线（2024年部分开通），强化站城融合。这将大幅提升线网密度、站点覆盖和换乘便利，让市民出行更高效，推动长三角一体化发展和城市功能升级。</p>
          </div>
          <div class="charts-grid">
            <div class="chart-container full-width">
              <div class="highlight-instruction" 
                   @mouseenter="handleTicketSystemHover(true)"
                   @mouseleave="handleTicketSystemHover(false)">
                <span class="hover-indicator">🖱️ 悬停此处查看线路图中未开通路段</span>
              </div>
            </div>
          </div>
        </section>

        <!-- 页脚 -->
        <footer class="footer">
          <div class="footer-content">
            <!-- 制作团队 -->
            <div class="footer-section team-section">
              <h3 class="footer-title">制作团队</h3>
              <div class="footer-text">
                <p><strong>策划：</strong>李响</p>
                <p><strong>数据分析：</strong>李响 袁金玉 姜婧雯</p>
                <p><strong>编辑：</strong>李响 袁金玉 姜婧雯 陆思涵</p>
                <p><strong>网页设计：</strong>陆思涵</p>
              </div>
            </div>

            <!-- 指导老师 -->
            <div class="footer-section advisor-section">
              <h3 class="footer-title">指导老师</h3>
              <div class="footer-text">
                <p>卞清老师</p>
              </div>
            </div>

            <!-- 数据来源 -->
            <div class="footer-section references-section">
              <h3 class="footer-title">数据资料来源</h3>
              <div class="footer-text">
                <p><strong>线路及车站信息：</strong>上海地铁官方网站</p>
                <p><strong>新闻报道：</strong>解放日报-上观新闻、新华网、东方网、汉网、微信公众号（新闻透视、看看新闻Knews、上海民声直通车）</p>
                <p><strong>官方文件：</strong>上海市人民政府、上海虹桥国际中央商务区管理委员会、上海市公安局、上海市规划和自然资源局、中国政府网、豆丁网</p>
                <p><strong>社交平台数据：</strong>小红书</p>
                <p><strong>地理数据：</strong>百度地图</p>
                <p><strong>地铁咨询与解读：</strong>微信公众号（数说DP、贵道蕉通、滴水湖、Metro Delta）</p>
              </div>
            </div>

            <!-- 底部信息 -->
            <div class="footer-bottom">
              <div class="footer-divider"></div>
              <div class="footer-meta">
                <p class="copyright">© 2025 第十届中国数据新闻大赛参赛作品 · 仅供学习研究使用</p>
                <p class="update-time">最后更新：2025年8月20日</p>
              </div>
            </div>
          </div>
        </footer>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted, nextTick, watch, provide } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollSmoother } from 'gsap/ScrollSmoother'
import { useMetroDataStore } from './stores/metroData'
import MetroToiletChart from './components/MetroToiletChart.vue'
import MetroSecurityTimeline from './components/MetroSecurityTimeline.vue'
import MetroTicketSystem from './components/MetroTicketSystem.vue'
import MetroRidingIssues from './components/MetroRidingIssues.vue'
import MetroSignageSystem from './components/MetroSignageSystem.vue'
import MetroGateChart from './components/MetroGateChart.vue'
import MetroCoverageComparison from './components/MetroCoverageComparison.vue'
import MetroStationDistanceChart from './components/MetroStationDistanceChart_Compact.vue'
import MetroTransferChart from './components/MetroTransferChart_Compact.vue'
import MetroOutTransferChart from './components/MetroOutTransferChart_Compact.vue'
import MetroConfusionWordCloud from './components/MetroConfusionWordCloud.vue'
import MetroNetworkBackground from './components/MetroNetworkBackground.vue'
import MetroSignGenerator from './components/MetroSignGenerator.vue'
import MetroAccessibilityChart from './components/MetroAccessibilityChart.vue'
import MetroWaitingChart from './components/MetroWaitingChart.vue'

// 注册GSAP插件
gsap.registerPlugin(ScrollTrigger, ScrollSmoother)

const metroStore = useMetroDataStore()

// 地铁线路颜色配置
const metroColors = ref<any>(null)
// 卫生间数据配置
const toiletDataRef = ref<any>(null)

// 根据滚动进度获取当前地铁线路颜色（不使用渐变）
const getCurrentMetroColor = computed(() => {
  if (!metroColors.value || !metroColors.value.data || !metroColors.value.data.lines) {
    return '#dc2626' // 默认红色
  }
  
  // 只使用1-18号线作为进度条颜色
  const allLines = metroColors.value.data.lines
  const lines = allLines.filter((line: any) => line.number >= 1 && line.number <= 18)
  const currentProgress = scrollProgress.value
  
  // 为了更平滑的切换，我们在每个线路区间内稍微延迟切换
  // 将进度分成更细的段，每段对应一个线路
  const segmentSize = 100 / lines.length
  const currentSegment = currentProgress / segmentSize
  
  // 使用一个小的缓冲区，让颜色切换更平滑
  const bufferZone = 0.2 // 20%的缓冲区
  const adjustedIndex = Math.floor(currentSegment + bufferZone)
  const clampedIndex = Math.min(Math.max(adjustedIndex, 0), lines.length - 1)
  
  return lines[clampedIndex].color
})

// 获取当前线路编号
const getCurrentLineNumber = computed(() => {
  if (!metroColors.value || !metroColors.value.data || !metroColors.value.data.lines) {
    return '1' // 默认1号线
  }
  
  // 只使用1-18号线作为进度条编号
  const allLines = metroColors.value.data.lines
  const lines = allLines.filter((line: any) => line.number >= 1 && line.number <= 18)
  const currentProgress = scrollProgress.value
  
  const segmentSize = 100 / lines.length
  const currentSegment = currentProgress / segmentSize
  const bufferZone = 0.2
  const adjustedIndex = Math.floor(currentSegment + bufferZone)
  const clampedIndex = Math.min(Math.max(adjustedIndex, 0), lines.length - 1)
  
  return lines[clampedIndex].number.toString()
})

// 滚动进度
const scrollProgress = ref(0)
const currentStation = ref('进站安检')
const showProgressBar = ref(false)
const networkVisible = ref(false) // 控制网络图显示
const showUnopenedSegments = ref(false) // 控制是否显示未开通线路段
const showMainContent = ref(false) // 控制主内容区域显示
const showTooltip = ref(false) // 控制即时提示显示
const metroSignGenerator = ref<InstanceType<typeof MetroSignGenerator> | null>(null) // MetroSignGenerator组件引用
const metroNetworkRef = ref<InstanceType<typeof MetroNetworkBackground> | null>(null) // MetroNetworkBackground组件引用
const ticketSystemContainer = ref<HTMLElement | null>(null) // 票务系统容器引用

// 自动隐藏定时器
let autoHideTimer: number | null = null
// 防抖定时器 - 悬停0.5秒后才触发
let hoverDebounceTimer: number | null = null
// 冷却时间状态 - 线路图消失后2秒内不再触发
let isCooldown = ref(false)
let cooldownTimer: number | null = null

// 禁用/启用页面滚动
const disablePageScroll = () => {
  // 禁用页面滚动
  document.body.style.overflow = 'hidden'
  // 禁用 ScrollSmoother
  if (smoother) {
    smoother.paused(true)
  }
}

const enablePageScroll = () => {
  // 恢复页面滚动
  document.body.style.overflow = ''
  // 恢复 ScrollSmoother
  if (smoother) {
    smoother.paused(false)
  }
}

// 处理MetroSignGenerator过渡完成事件
const handleTransitionComplete = () => {
  showMainContent.value = true
}

// 章节数据配置（中英文对照）
const sectionData = [
  { chinese: '进站安检', english: 'Entrance', id: 'security-section' },
  { chinese: '过闸机', english: 'Gate', id: 'gate-section' },
  { chinese: '站台等候', english: 'Platform', id: 'waiting-section' },
  { chinese: '乘车', english: 'Riding', id: 'riding-section' },
  { chinese: '换乘', english: 'Transfer', id: 'transfer-section' },
  { chinese: '线路网', english: 'Network', id: 'route-section' },
]

// 地铁站点名称映射 - 基于章节数据生成
const stationNames = sectionData.map(section => section.chinese)

// 当前显示的章节
const currentSectionIndex = ref(0)

// 切换网络图显示
const toggleNetworkVisibility = () => {
  const previousVisible = networkVisible.value
  networkVisible.value = !networkVisible.value
  
  // 根据显示状态控制页面滚动
  if (networkVisible.value) {
    disablePageScroll() // 显示时禁用滚动
  } else {
    enablePageScroll() // 隐藏时恢复滚动
  }
  
  
  // 确保按钮在切换后仍然可见
  nextTick(() => {
    const controlWrapper = document.querySelector('.metro-control-wrapper')
    if (controlWrapper) {
    } else {
    }
  })
}

// 跳转到指定站点
const jumpToStation = (stationName: string) => {
  
  // 首先显示线路图
  if (!networkVisible.value) {
    networkVisible.value = true
    disablePageScroll()
  }
  
  // 等待线路图完全加载后再定位站点
  nextTick(() => {
    setTimeout(() => {
      if (metroNetworkRef.value && metroNetworkRef.value.centerOnStation) {
        const success = metroNetworkRef.value.centerOnStation(stationName)
        if (success) {
        } else {
        }
      } else {
      }
    }, networkVisible.value ? 100 : 500) // 如果已经显示则短延迟，否则长延迟
  })
}

// 高亮线路段功能
const highlightLineSegment = (fromStation: string, toStation: string, lineName: string) => {
  
  // 首先显示线路图
  if (!networkVisible.value) {
    networkVisible.value = true
    disablePageScroll()
  }
  
  // 等待线路图完全加载后再高亮线路段
  nextTick(() => {
    setTimeout(() => {
      if (metroNetworkRef.value && metroNetworkRef.value.highlightLineSegment) {
        const success = metroNetworkRef.value.highlightLineSegment(fromStation, toStation, lineName)
        if (success) {
        } else {
          // 如果高亮失败，尝试跳转到起点站
          if (metroNetworkRef.value.centerOnStation) {
            metroNetworkRef.value.centerOnStation(fromStation)
          }
        }
      } else {
        // 如果方法不可用，尝试跳转到起点站
        jumpToStation(fromStation)
      }
    }, networkVisible.value ? 100 : 500) // 如果已经显示则短延迟，否则长延迟
  })
}

// 新增：检测下拉刷新式手势状态
const isPullToRefresh = ref(false)
const pullStartY = ref(0)
const pullCurrentY = ref(0)
const pullDistance = ref(0)
const pullThreshold = ref(80) // 下拉阈值（像素）
const isAtTop = ref(true)
const hasTriggeredAnimation = ref(false)

// 检测触摸开始
const handleTouchStart = (e: TouchEvent) => {
  const scrollY = smoother ? smoother.scrollTop() : (window.pageYOffset || document.documentElement.scrollTop)
  isAtTop.value = scrollY <= 5
  
  if (isAtTop.value) {
    pullStartY.value = e.touches[0].clientY
    isPullToRefresh.value = true
  }
}

// 检测触摸移动
const handleTouchMove = (e: TouchEvent) => {
  if (!isPullToRefresh.value || !isAtTop.value) return
  
  pullCurrentY.value = e.touches[0].clientY
  pullDistance.value = pullCurrentY.value - pullStartY.value
  
  // 只有向下拉（正值）且超过阈值才触发
  if (pullDistance.value > pullThreshold.value && !hasTriggeredAnimation.value) {
    hasTriggeredAnimation.value = true
    
    // 触发开场动画
    if (showMainContent.value && metroSignGenerator.value && !networkVisible.value) {
      showMainContent.value = false
      setTimeout(() => {
        if (metroSignGenerator.value) {
          metroSignGenerator.value.restartAnimation()
        }
      }, 300)
    }
  }
}

// 检测触摸结束
const handleTouchEnd = () => {
  isPullToRefresh.value = false
  pullStartY.value = 0
  pullCurrentY.value = 0
  pullDistance.value = 0
  // 延迟重置动画触发状态，避免连续触发
  setTimeout(() => {
    hasTriggeredAnimation.value = false
  }, 2000)
}

// 桌面端：检测鼠标滚轮向上滚动超过顶部
const handleWheel = (e: WheelEvent) => {
  const scrollY = smoother ? smoother.scrollTop() : (window.pageYOffset || document.documentElement.scrollTop)
  
  // 检测是否在页面顶部且向上滚动
  if (scrollY <= 5 && e.deltaY < 0 && !hasTriggeredAnimation.value) {
    hasTriggeredAnimation.value = true
    
    // 触发开场动画
    if (showMainContent.value && metroSignGenerator.value && !networkVisible.value) {
      showMainContent.value = false
      setTimeout(() => {
        if (metroSignGenerator.value) {
          metroSignGenerator.value.restartAnimation()
        }
      }, 300)
    }
    
    // 延迟重置动画触发状态
    setTimeout(() => {
      hasTriggeredAnimation.value = false
    }, 2000)
  }
}

// 向子组件提供跳转方法
provide('jumpToStation', jumpToStation)
provide('highlightLineSegment', highlightLineSegment)

// 处理票务系统悬停事件
const handleTicketSystemHover = (isHovering: boolean) => {
  if (isHovering) {
    // 悬停进入：检查冷却状态
    if (isCooldown.value) {
      return
    }
    
    // 清除之前的防抖定时器
    if (hoverDebounceTimer) {
      clearTimeout(hoverDebounceTimer)
    }
    
    // 设置0.5秒防抖，只有持续悬停0.5秒才触发
    hoverDebounceTimer = setTimeout(() => {
      // 显示线路图和未开通线路段
      networkVisible.value = true
      showUnopenedSegments.value = true
      disablePageScroll() // 禁用页面滚动
      
      // 清除之前的自动隐藏定时器
      if (autoHideTimer) {
        clearTimeout(autoHideTimer)
      }
      
      // 设置5秒后自动隐藏
      autoHideTimer = setTimeout(() => {
        networkVisible.value = false
        showUnopenedSegments.value = false
        enablePageScroll() // 恢复页面滚动
        autoHideTimer = null
        
        // 开始2秒冷却时间
        isCooldown.value = true
        cooldownTimer = setTimeout(() => {
          isCooldown.value = false
          cooldownTimer = null
        }, 2000) // 2秒冷却时间
        
      }, 5000) // 5秒 = 5000毫秒
      
      hoverDebounceTimer = null
    }, 500) // 0.5秒防抖时间
    
  } else {
    // 悬停离开：清除防抖定时器，但不影响已经显示的线路图
    if (hoverDebounceTimer) {
      clearTimeout(hoverDebounceTimer)
      hoverDebounceTimer = null
    } else if (networkVisible.value) {
    }
  }
}

// 基于ScrollTrigger的进度更新函数
const updateScrollProgressFromTrigger = (triggerProgress: number) => {
  const sections = document.querySelectorAll('.data-section')
  const footer = document.querySelector('.footer')
  
  if (sections.length === 0) return
  
  // 获取第一个data-section（进站安检）的位置
  const firstSection = sections[0]
  const scrollTop = smoother ? smoother.scrollTop() : (window.pageYOffset || document.documentElement.scrollTop)
  const firstSectionRect = firstSection.getBoundingClientRect()
  const firstSectionTop = firstSectionRect.top + scrollTop
  
  // 检查是否进入页脚区域
  let hasEnteredFooter = false
  if (footer) {
    const footerRect = footer.getBoundingClientRect()
    const footerTop = footerRect.top + scrollTop
    // 当滚动位置达到页脚顶部时，开始隐藏章节指示器
    hasEnteredFooter = scrollTop + window.innerHeight / 2 >= footerTop
  }
  
  // 确保指示框有足够空间显示（距离顶部至少80px）
  const hasReachedFirstSection = scrollTop + window.innerHeight / 2 >= firstSectionTop
  const hasEnoughSpace = scrollTop >= 80
  const shouldShowProgressBar = hasReachedFirstSection && hasEnoughSpace && !hasEnteredFooter
  
  showProgressBar.value = shouldShowProgressBar
  
  if (!shouldShowProgressBar) {
    scrollProgress.value = 0
    currentStation.value = '进站安检'
    currentSectionIndex.value = -1  // 隐藏章节指示器
    return
  }
  
  // 使用触发器进度来计算滚动进度
  const lastSection = sections[sections.length - 1]
  const lastSectionRect = lastSection.getBoundingClientRect()
  const lastSectionBottom = lastSectionRect.top + lastSectionRect.height + scrollTop
  
  const sectionAreaHeight = lastSectionBottom - firstSectionTop
  const progressInSections = Math.max(0, scrollTop + window.innerHeight / 2 - firstSectionTop)
  const progress = sectionAreaHeight > 0 ? (progressInSections / sectionAreaHeight) * 100 : 0
  scrollProgress.value = Math.min(100, Math.max(0, progress))
  
  // 根据section-title的位置更新当前站点和章节
  let currentIndex = 0
  sections.forEach((section, index) => {
    const sectionTitle = section.querySelector('.section-title')
    if (sectionTitle) {
      const titleRect = sectionTitle.getBoundingClientRect()
      const titleTop = titleRect.top + scrollTop
      
      // 当标题位置达到屏幕中央时切换章节
      if (scrollTop + window.innerHeight / 2 >= titleTop) {
        currentIndex = index
      }
    }
  })
  
  currentStation.value = stationNames[currentIndex]
  currentSectionIndex.value = currentIndex  // 更新当前章节索引
}

// 更新滚动进度
const updateScrollProgress = () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const sections = document.querySelectorAll('.data-section')
  const footer = document.querySelector('.footer')
  
  if (sections.length === 0) return
  
  // 获取第一个data-section（进站安检）的位置
  const firstSection = sections[0]
  const firstSectionRect = firstSection.getBoundingClientRect()
  const firstSectionTop = firstSectionRect.top + scrollTop
  
  // 检查是否进入页脚区域
  let hasEnteredFooter = false
  if (footer) {
    const footerRect = footer.getBoundingClientRect()
    const footerTop = footerRect.top + scrollTop
    // 当滚动位置达到页脚顶部时，开始隐藏章节指示器
    hasEnteredFooter = scrollTop + window.innerHeight / 2 >= footerTop
  }
  
  // 确保指示框有足够空间显示（距离顶部至少80px）
  // 只有当滚动到第一个章节且有足够空间且未进入页脚时才显示进度条
  const hasReachedFirstSection = scrollTop + window.innerHeight / 2 >= firstSectionTop
  const hasEnoughSpace = scrollTop >= 80 // 确保指示框不会被遮挡
  const shouldShowProgressBar = hasReachedFirstSection && hasEnoughSpace && !hasEnteredFooter
  
  showProgressBar.value = shouldShowProgressBar
  
  if (!shouldShowProgressBar) {
    scrollProgress.value = 0
    currentStation.value = '进站安检'
    return
  }
  
  // 计算从第一个section开始的进度
  const lastSection = sections[sections.length - 1]
  const lastSectionRect = lastSection.getBoundingClientRect()
  const lastSectionBottom = lastSectionRect.top + lastSectionRect.height + scrollTop
  
  const sectionAreaHeight = lastSectionBottom - firstSectionTop
  const progressInSections = Math.max(0, scrollTop + window.innerHeight / 2 - firstSectionTop)
  const progress = sectionAreaHeight > 0 ? (progressInSections / sectionAreaHeight) * 100 : 0
  scrollProgress.value = Math.min(100, Math.max(0, progress))
  
  // 根据section-title的位置更新当前站点
  let currentIndex = 0
  sections.forEach((section, index) => {
    const sectionTitle = section.querySelector('.section-title')
    if (sectionTitle) {
      const titleRect = sectionTitle.getBoundingClientRect()
      const titleTop = titleRect.top + scrollTop
      
      // 当标题位置达到屏幕中央时切换章节
      if (scrollTop + window.innerHeight / 2 >= titleTop) {
        currentIndex = index
      }
    }
  })
  
  currentStation.value = stationNames[currentIndex]
}

// ScrollSmoother 实例
let smoother: any = null

// 生命周期钩子
onMounted(() => {
  // 初始化GSAP平滑滚动
  smoother = ScrollSmoother.create({
    wrapper: "#smooth-wrapper",
    content: "#smooth-content",
    smooth: 1.5,   // 平滑强度
    effects: true, // 启用数据效果
    smoothTouch: 0.1, // 移动端触摸平滑度（较低值以保持响应性）
    normalizeScroll: true, // 标准化滚动
    ignoreMobileResize: true, // 忽略移动端尺寸变化
  })

  // 加载上海地铁颜色数据
  fetch('/shanghai-metro-colors.json')
    .then(response => response.json())
    .then(data => {
      metroColors.value = data
    })
    .catch(error => {
    })

  // 加载卫生间数据
  fetch('/toilet-data.json')
    .then(response => response.json())
    .then(data => {
      toiletDataRef.value = data
    })
    .catch(error => {
    })
  
  // 使用ScrollTrigger而不是原生scroll事件来更好地配合平滑滚动
  ScrollTrigger.create({
    trigger: "body",
    start: "top top",
    end: "bottom bottom",
    onUpdate: self => {
      const progress = self.progress * 100
      updateScrollProgressFromTrigger(progress)
      
      // 持续保存滚动位置到localStorage
      const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
      localStorage.setItem('shmetro_scroll_position', scrollY.toString())
    }
  })
  
  // 添加触摸事件监听器（移动端）
  document.addEventListener('touchstart', handleTouchStart, { passive: true })
  document.addEventListener('touchmove', handleTouchMove, { passive: true })
  document.addEventListener('touchend', handleTouchEnd, { passive: true })
  
  // 添加滚轮事件监听器（桌面端）
  document.addEventListener('wheel', handleWheel, { passive: true })
  
  updateScrollProgress()
})

// 监听主内容显示状态变化，确保按钮可见性
watch(showMainContent, (newValue, oldValue) => {
})

onUnmounted(() => {
  // 清理ScrollSmoother和ScrollTrigger
  if (smoother) {
    smoother.kill()
  }
  ScrollTrigger.killAll()
  
  // 清理所有定时器
  if (autoHideTimer) {
    clearTimeout(autoHideTimer)
    autoHideTimer = null
  }
  if (hoverDebounceTimer) {
    clearTimeout(hoverDebounceTimer)
    hoverDebounceTimer = null
  }
  if (cooldownTimer) {
    clearTimeout(cooldownTimer)
    cooldownTimer = null
  }
  
  // 清理事件监听器
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)
  document.removeEventListener('wheel', handleWheel)
  window.removeEventListener('scroll', updateScrollProgress)
  
  // 确保恢复页面滚动
  enablePageScroll()
})

// 卫生间数据
const toiletData = computed(() => {
  if (!toiletDataRef.value || !toiletDataRef.value.fullData) {
    return []
  }
  return toiletDataRef.value.fullData
})
</script>


<style scoped>
/* 导入思源字体 */
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@100;300;400;500;700;900&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@200;300;400;500;600;700;900&display=swap');

/* section-description 使用思源宋体 - 高优先级 */
.section-description,
.section-description p,
.section-description * {
  font-family: 'Noto Serif SC', 'Source Han Serif SC', 'Songti SC', '宋体', serif !important;
  font-weight: 400 !important;
  line-height: 1.9 !important;
  font-size: 16px !important;
  color: #374151 !important;
  text-align: justify !important;
  text-justify: inter-ideograph !important;
  letter-spacing: 0.3px !important;
  margin: 0 0 1.2em 0 !important;
  text-indent: 2em !important;
}

/* 段落间距优化 */
.section-description p:last-child {
  margin-bottom: 0 !important;
}

/* 文本高亮样式 - 用于手动标记重要内容，模仿荧光笔效果 */
.highlight {
  background-color: #dc2626 !important;
  color: #ffffff !important;
  margin: 0 4px !important;
  padding: 2px 6px !important;
  border-radius: 0 !important;
  font-weight: 500 !important;
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.3) !important;
  transition: all 0.2s ease !important;
}

.highlight:hover {
  background-color: #b91c1c !important;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.4) !important;
  transform: translateY(-1px) !important;
}

/* 节标题样式优化 */
.section-title {
  font-family: 'Noto Sans SC', 'Source Han Sans SC', 'PingFang SC', 'Microsoft YaHei', '微软雅黑', sans-serif !important;
  font-size: 32px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  margin: 0 0 8px 0 !important;
  letter-spacing: 1px !important;
  line-height: 1.3 !important;
}

/* 副标题样式美化 */
.section-subtitle {
  font-family: 'Noto Sans SC', 'Source Han Sans SC', 'PingFang SC', 'Microsoft YaHei', '微软雅黑', sans-serif !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  color: #2563eb !important;
  letter-spacing: 0.5px !important;
  line-height: 1.4 !important;
  position: relative !important;
  padding-left: 12px !important;
}

/* 副标题前的装饰线 */
.section-subtitle::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 18px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 2px;
}

/* 节标题容器样式 */
.section-header {
  margin-top: 12px !important;
  margin-bottom: 12px !important;
}

/* 确保其他文本元素明确使用思源黑体 */
.section-chinese,
.section-english,
.metro-line-indicator,
.instant-tooltip,
h1, h2, h3, h4, h5, h6,
.chart-container h3,
.footer-content {
  font-family: 'Noto Sans SC', 'Source Han Sans SC', 'PingFang SC', 'Microsoft YaHei', '微软雅黑', sans-serif !important;
}

/* 主内容平滑显示控制 */
#smooth-wrapper {
  opacity: 0;
  pointer-events: none;
  transition: opacity 1s ease-in-out;
}

#smooth-wrapper.content-visible {
  opacity: 1;
  pointer-events: auto;
}

/* 控制按钮过渡效果 */
.control-fade-enter-active,
.control-fade-leave-active {
  transition: all 0.8s ease;
}

.control-fade-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.9);
}

.control-fade-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.9);
}

/* 章节指示器过渡动画 */
.section-indicator-fade-enter-active {
  transition: all 0.6s ease-out;
}

.section-indicator-fade-leave-active {
  transition: all 0.4s ease-in;
}

.section-indicator-fade-enter-from {
  opacity: 0;
  transform: translateY(-50%) translateX(-100%);
}

.section-indicator-fade-leave-to {
  opacity: 0;
  transform: translateY(-50%) translateX(-100%);
}

/* 即时提示过渡效果 */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: all 0.2s ease;
}

.tooltip-fade-enter-from {
  opacity: 0;
  transform: translateY(10px) scale(0.8);
}

.tooltip-fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.8);
}

/* 线路图控制按钮 */
.metro-control-wrapper {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 999999;
  pointer-events: auto;
  /* 确保按钮始终可见，不被任何元素遮挡 */
  isolation: isolate;
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.toggle-button-container,
.position-button-container {
  position: relative;
  display: inline-block;
  /* 确保容器也有足够高的层级 */
  z-index: 1000000;
}

.position-toggle-btn {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid #0085C3;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  color: #0085C3;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.position-toggle-btn:hover {
  background: #0085C3;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.instant-tooltip {
  position: absolute;
  bottom: 120px; /* 位于按钮上方 */
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.instant-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid rgba(0, 0, 0, 0.9);
}

.metro-network-toggle {
  width: 100px !important;
  height: 100px !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3)) !important;
  opacity: 0.9 !important;
  /* 确保按钮图标也在最高层级 */
  position: relative !important;
  z-index: 1000001 !important;
}

.metro-network-toggle:hover {
  transform: translateY(-2px) scale(1.1) !important;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.4)) !important;
  opacity: 1 !important;
}

.toggle-icon {
  width: 28px;
  height: 28px;
  filter: brightness(0) invert(1); /* 将图标变为白色 */
  transition: transform 0.3s ease;
}

.metro-network-toggle:hover .toggle-icon {
  transform: scale(1.1) rotate(5deg);
}

/* 数据章节整体样式 */
.data-section {
  margin-bottom: 80px !important;
  padding: 40px 32px !important;
  background: rgba(255, 255, 255, 0.7) !important;
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(59, 130, 246, 0.15) !important;
  transition: all 0.3s ease !important;
}

.data-section:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  border-color: rgba(59, 130, 246, 0.3) !important;
}

/* 图表容器样式优化 */
.charts-grid {
  margin-top: 24px !important;
}

.chart-container {
  background: rgba(255, 255, 255, 0.9) !important;
  border-radius: 12px !important;
  padding: 24px !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid rgba(59, 130, 246, 0.1) !important;
  margin-bottom: 20px !important;
  position: relative !important;
}

.chart-container h3 {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #1f2937 !important;
  margin-bottom: 16px !important;
  text-align: center !important;
  letter-spacing: 0.5px !important;
}

/* 应用整体容器 */
.app {
  max-width: 1200px !important;
  margin: 0 auto !important;
  padding: 20px !important;
}

/* 页脚样式 */
.footer {
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.95) 0%, 
    rgba(30, 41, 59, 0.95) 50%, 
    rgba(51, 65, 85, 0.95) 100%) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 20px !important;
  color: #e2e8f0 !important;
  position: relative !important;
  overflow: hidden !important;
  z-index: -1 !important;
}

.footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(147, 197, 253, 0.08) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.footer-content {
  position: relative;
  z-index: 1;
  max-width: 1200px !important;
  margin: 0 auto !important;
  padding: 50px 40px 20px 40px !important;
}

/* 页脚章节布局 */
.footer-section {
  margin-bottom: 36px !important;
}

.footer-title {
  font-size: 22px !important;
  font-weight: 700 !important;
  color: #f8fafc !important;
  margin-bottom: 20px !important;
  letter-spacing: 0.5px !important;
  text-align: center !important;
}

/* 页脚正文样式 */
.footer-text {
  max-width: 800px;
}

.footer-text p {
  font-family: 'Noto Serif SC', 'Source Han Serif SC', 'Songti SC', '宋体', serif !important;
  font-size: 18px !important;
  line-height: 1.8 !important;
  color: #cbd5e1 !important;
  margin: 8px 0 !important;
  text-align: center !important;
  letter-spacing: 0.3px !important;
  text-indent: 0 !important;
}

/* 制作团队样式 */
.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.team-member {
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: rgba(255, 255, 255, 0.08) !important;
  border-radius: 12px;
  border: 1px solid rgba(59, 130, 246, 0.2);
  transition: all 0.3s ease;
}

.team-member:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.member-name {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #f1f5f9 !important;
  margin-bottom: 6px !important;
}

.member-role {
  font-size: 14px !important;
  color: #94a3b8 !important;
  font-weight: 400 !important;
}

/* 指导老师样式 */
.advisor-list {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.advisor-item {
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: rgba(59, 130, 246, 0.1) !important;
  border-radius: 12px;
  border: 1px solid rgba(59, 130, 246, 0.3);
  min-width: 280px;
  transition: all 0.3s ease;
}

.advisor-item:hover {
  background: rgba(59, 130, 246, 0.15) !important;
  border-color: rgba(59, 130, 246, 0.5);
  transform: translateY(-2px);
}

.advisor-name {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: #f8fafc !important;
  margin-bottom: 8px !important;
}

.advisor-title {
  font-size: 15px !important;
  color: #cbd5e1 !important;
  font-weight: 400 !important;
}

/* 参考文献样式 */
.references-list {
  display: grid;
  gap: 16px;
}

.reference-item {
  display: flex;
  flex-direction: column;
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.05) !important;
  border-radius: 8px;
  border-left: 4px solid rgba(59, 130, 246, 0.6);
  transition: all 0.3s ease;
}

.reference-item:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  border-left-color: rgba(59, 130, 246, 0.8);
  transform: translateX(4px);
}

.reference-title {
  font-size: 16px !important;
  font-weight: 500 !important;
  color: #e2e8f0 !important;
  margin-bottom: 6px !important;
  line-height: 1.4 !important;
}

.reference-source {
  font-size: 14px !important;
  color: #94a3b8 !important;
  font-style: italic !important;
}

/* 页脚底部 */
.footer-bottom {
  margin-top: 48px !important;
}

.footer-divider {
  height: 1px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(59, 130, 246, 0.4) 20%, 
    rgba(59, 130, 246, 0.6) 50%, 
    rgba(59, 130, 246, 0.4) 80%, 
    transparent 100%);
  margin-bottom: 24px;
}

.footer-meta {
  text-align: center;
  padding: 20px 0;
}

.footer-meta p {
  margin: 8px 0 !important;
  font-size: 14px !important;
  color: #94a3b8 !important;
}

.copyright {
  font-weight: 500 !important;
  color: #cbd5e1 !important;
}

.build-info {
  color: #64748b !important;
}

.update-time {
  color: #64748b !important;
  font-size: 13px !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .footer-content {
    padding: 40px 20px 30px 20px !important;
  }
  
  .team-grid {
    grid-template-columns: 1fr;
  }
  
  .advisor-list {
    flex-direction: column;
  }
  
  .advisor-item {
    min-width: auto;
  }
  
  .footer-title {
    font-size: 20px !important;
  }
}

/* 全宽度图表容器 */
.chart-container.full-width {
  grid-column: 1 / -1;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .section-title {
    font-size: 28px !important;
  }
  
  .section-subtitle {
    font-size: 18px !important;
  }
  
  .section-description,
  .section-description p {
    font-size: 15px !important;
    line-height: 1.8 !important;
  }
  
  .data-section {
    padding: 24px 20px !important;
    margin-bottom: 60px !important;
  }
  
  .section-header {
    margin-bottom: 24px !important;
  }
  
  .app {
    padding: 16px !important;
  }
}

/* 页面背景样式 */
.page-background {
  position: relative;
  overflow: hidden;
  background: rgba(248, 250, 252, 0.6) !important; /* 半透明背景 */
}

/* 网格动效层 - 设置在最底层 */
.page-background::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, rgba(59, 130, 246, 0.25) 1px, transparent 1px),
    linear-gradient(0deg, rgba(59, 130, 246, 0.25) 1px, transparent 1px),
    linear-gradient(45deg, rgba(147, 197, 253, 0.18) 1px, transparent 1px),
    linear-gradient(-45deg, rgba(147, 197, 253, 0.18) 1px, transparent 1px),
    linear-gradient(135deg, rgba(96, 165, 250, 0.12) 1px, transparent 1px),
    linear-gradient(-135deg, rgba(96, 165, 250, 0.12) 1px, transparent 1px);
  background-size: 
    30px 30px,
    30px 30px,
    60px 60px,
    60px 60px,
    90px 90px,
    90px 90px;
  animation: grid-flow 18s linear infinite, grid-pulse 12s ease-in-out infinite;
  pointer-events: none;
  z-index: -100; /* 最底层 */
}

/* 脉冲点效果层 */
.page-background::after {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.15) 3px, transparent 3px),
    radial-gradient(circle at 75% 75%, rgba(147, 197, 253, 0.12) 4px, transparent 4px),
    radial-gradient(circle at 50% 10%, rgba(96, 165, 250, 0.08) 2px, transparent 2px),
    radial-gradient(circle at 10% 80%, rgba(30, 144, 255, 0.1) 2.5px, transparent 2.5px),
    radial-gradient(circle at 90% 20%, rgba(0, 123, 255, 0.06) 1.5px, transparent 1.5px),
    radial-gradient(circle at 30% 60%, rgba(138, 43, 226, 0.04) 1px, transparent 1px);
  background-size: 
    70px 70px,
    110px 110px,
    40px 40px,
    85px 85px,
    55px 55px,
    130px 130px;
  animation: metro-pulse 15s ease-in-out infinite, station-blink 8s ease-in-out infinite;
  pointer-events: none;
  z-index: -99; /* 倒数第二层 */
}

/* 主内容容器样式 */
.content-container {
  background: linear-gradient(135deg, 
    rgba(240, 249, 255, 0.85) 0%, 
    rgba(219, 234, 254, 0.8) 30%, 
    rgba(186, 230, 253, 0.75) 70%, 
    rgba(147, 197, 253, 0.7) 100%) !important;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(59, 130, 246, 0.2);
  position: relative;
  z-index: 1; /* 确保在网格动效之上 */
}

@keyframes grid-flow {
  0% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 1;
    filter: brightness(1);
  }
  25% {
    transform: translateX(-40px) translateY(20px) rotate(1deg);
    opacity: 0.9;
    filter: brightness(1.2);
  }
  50% {
    transform: translateX(-80px) translateY(0) rotate(0deg);
    opacity: 1;
    filter: brightness(1.1);
  }
  75% {
    transform: translateX(-40px) translateY(-20px) rotate(-1deg);
    opacity: 0.9;
    filter: brightness(1.3);
  }
  100% {
    transform: translateX(0) translateY(0) rotate(0deg);
    opacity: 1;
    filter: brightness(1);
  }
}

@keyframes metro-pulse {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1) rotate(0deg);
    filter: brightness(1);
  }
  33% {
    opacity: 1;
    transform: scale(1.08) rotate(1deg);
    filter: brightness(1.3);
  }
  66% {
    opacity: 0.5;
    transform: scale(0.95) rotate(-0.5deg);
    filter: brightness(0.9);
  }
}

/* 新增：站点闪烁动画 */
@keyframes station-blink {
  0%, 90%, 100% {
    opacity: 1;
  }
  45% {
    opacity: 0.2;
  }
}

/* 新增：网格脉冲动画 */
@keyframes grid-pulse {
  0%, 100% {
    filter: brightness(1) hue-rotate(0deg);
  }
  50% {
    filter: brightness(1.4) hue-rotate(10deg);
  }
}

/* 新增：地铁线路流动动画 */
@keyframes metro-line-flow {
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
}

/* 章节指示器过渡动画 */
.progress-fade-enter-active, .progress-fade-leave-active {
  transition: opacity 0.4s ease-in-out, transform 0.4s ease-in-out;
}
.progress-fade-enter-from, .progress-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 线路指示器过渡动画 */
.line-indicator-fade-enter-active, .line-indicator-fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.line-indicator-fade-enter-from, .line-indicator-fade-leave-to {
  opacity: 0;
}

/* 闸机图片展示样式 */
.gate-images-gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 16px;
}

.gate-image-item {
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(59, 130, 246, 0.2) !important;
  transition: all 0.3s ease !important;
}

.gate-image-item:hover {
  transform: translateY(-4px) !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15) !important;
  border-color: rgba(59, 130, 246, 0.4) !important;
}

.gate-image {
  width: 100% !important;
  height: 200px !important;
  object-fit: cover !important;
  object-position: center !important;
  transition: transform 0.3s ease !important;
}

.gate-image-item:hover .gate-image {
  transform: scale(1.05) !important;
}

.gate-image-caption {
  padding: 16px 20px !important;
}

.gate-image-caption h4 {
  font-family: 'Noto Sans SC', 'Source Han Sans SC', 'PingFang SC', 'Microsoft YaHei', '微软雅黑', sans-serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1f2937 !important;
  margin: 0 0 8px 0 !important;
  letter-spacing: 0.5px !important;
}

.gate-image-caption p {
  font-family: 'Noto Serif SC', 'Source Han Serif SC', 'Songti SC', '宋体', serif !important;
  font-size: 14px !important;
  line-height: 1.6 !important;
  color: #6b7280 !important;
  margin: 0 !important;
  text-indent: 0 !important;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .gate-images-gallery {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }
}

@media (max-width: 768px) {
  .gate-images-gallery {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .gate-image {
    height: 180px !important;
  }
  
  .gate-image-caption {
    padding: 12px 16px !important;
  }
  
  .gate-image-caption h4 {
    font-size: 15px !important;
  }
  
  .gate-image-caption p {
    font-size: 13px !important;
  }
}

/* 资料来源注释样式 */
.data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.2);
  backdrop-filter: blur(4px);
  z-index: 10;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
}

/* 悬停指示样式 */
.highlight-instruction {
  margin-top: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border: 2px dashed #0ea5e9;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.highlight-instruction:hover {
  background: linear-gradient(135deg, #e0f2fe, #bae6fd);
  border-color: #0284c7;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.hover-indicator {
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: #0369a1;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  letter-spacing: 0.5px;
}

.highlight-instruction:hover .hover-indicator {
  color: #075985;
}
</style>
